/**
 * The texture library's picker: a button showing the chosen set (its
 * thumbnail, name and size), and on it a modal with every set as a tile —
 * seen, not read off a list — with a search box to narrow them. "None"
 * (the renderer's own finish, a staged floor) is the first tile when the
 * surface may do without a set.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Image as ImageIcon } from 'lucide-react';
import type { TextureSet } from '../../props/studioApi';
import { textureMapUrl } from '../../props/studioApi';
import { DesignerModal } from '../chrome/DesignerModal';
import { DESIGNER_STRINGS as S } from '../strings';

const P = S.photo;

/** A set's picture: its thumbnail, or its colour map. */
export const setThumb = (t: TextureSet): string | null => t.thumbnail ?? (t.maps.diffuse ? textureMapUrl(t.id, t.maps.diffuse) : null);
/** A set's size, feet, as the pickers show it. */
export const setSize = (t: TextureSet): string => P.textureSize(t.dimensions[0] / 304.8, t.dimensions[1] / 304.8);

export interface TexturePickerProps {
  sets: TextureSet[];
  /** The chosen set's id; '' or null for none. */
  value: string | null | undefined;
  /** What none is called (the renderer's own finish, a staged floor). Absent: a set must be chosen. */
  noneLabel?: string;
  /** The picker's name (its label, the modal's title). */
  label: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  /** The button's style (the row it sits in). */
  style?: React.CSSProperties;
}

export const TexturePicker: React.FC<TexturePickerProps> = ({ sets, value, noneLabel, label, onChange, disabled, style }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => { if (open) setQuery(''); }, [open]);
  const id = value ?? '';
  const current = sets.find((t) => t.id === id);
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (q ? sets.filter((t) => `${t.name} ${t.id} ${t.source}`.toLowerCase().includes(q)) : sets), [sets, q]);
  const pick = (next: string) => { onChange(next); setOpen(false); };
  const thumb = current ? setThumb(current) : null;
  const text = current ? `${current.name} · ${setSize(current)}` : id ? id : noneLabel ?? P.texturePickNone;
  return (
    <>
      <button
        type="button" className="rc-pick" style={style} disabled={disabled} onClick={() => setOpen(true)}
        aria-label={label} aria-haspopup="dialog" title={`${label}: ${text}. ${P.texturePickHint}`}
      >
        {thumb ? <img className="rc-pick-thumb" src={thumb} alt="" /> : <span className="rc-pick-thumb is-none"><ImageIcon size={12} /></span>}
        <span className="rc-pick-text">{text}</span>
        <ChevronDown size={12} style={{ flex: '0 0 auto', opacity: 0.7 }} />
      </button>
      {open && (
        <DesignerModal title={`${P.texturePick}: ${label}`} onClose={() => setOpen(false)} width={760} icon={<ImageIcon size={16} color="#FEB100" />}>
          <input
            className="rc-pick-search" type="search" value={query} placeholder={P.texturePickSearch} aria-label={P.texturePickSearch}
            autoFocus onChange={(e) => setQuery(e.target.value)}
          />
          <div className="rc-grid lf-wide rc-pick-grid" role="listbox" aria-label={label}>
            {noneLabel !== undefined && !q && (
              <button type="button" role="option" aria-selected={!id} className={`rc-tile rc-pick-none${!id ? ' is-active' : ''}`} onClick={() => pick('')} title={noneLabel}>
                <span className="rc-tile-name">{noneLabel}</span>
              </button>
            )}
            {shown.map((t) => {
              const pic = setThumb(t);
              return (
                <button
                  key={t.id} type="button" role="option" aria-selected={t.id === id} className={`rc-tile${t.id === id ? ' is-active' : ''}`}
                  style={{ backgroundImage: pic ? `url(${pic})` : undefined }} onClick={() => pick(t.id)} title={`${t.name} · ${setSize(t)}`}
                >
                  <span className="rc-tile-name">{t.name} <span style={{ opacity: 0.7 }}>{setSize(t)}</span></span>
                </button>
              );
            })}
          </div>
          {shown.length === 0 && <p className="rc-note" style={{ marginTop: 8 }}>{sets.length ? P.texturePickNoMatch : P.textureEmpty}</p>}
        </DesignerModal>
      )}
    </>
  );
};

export default TexturePicker;
